<script lang="ts" setup>
import { computed, watch, ref, onMounted} from "vue";
import { SkuDetails } from "./sizePicker.models";
import { useProductPageStore } from "../../stores/productPageStore";
import Icon from "../../components/icon/icon.vue";
import { IconSizes, IconTypes } from "../../components/icon/icon.model";
import { formatSize } from "../../mixins/productSize.mixin";

const props = defineProps<{
  matrixSizes: Record<string, any>;
  isSizeGuide?: boolean;
  showInventoryNotification?: boolean;
}>();

const productPageStore = useProductPageStore();

const displayWidthNotSelectedError = computed(
  () => productPageStore.displayWidthNotSelectedError,
);

const displayLengthNotSelectedError = computed(
  () => productPageStore.displayLengthNotSelectedError,
);

const pantsLengths = computed(() => {
  if (!props.matrixSizes) return [];
  let lengths = new Set<string>();

  props.matrixSizes?.forEach((skuDetailsArray) => {
    skuDetailsArray.forEach((skuDetails) => {
      lengths.add(skuDetails.matrixSizeL);
    });
  });

  return Array.from(lengths).sort((a, b) => Number(a) - Number(b));
});

const pantsWidths = computed(() => {
  if (!props.matrixSizes) return [];
  let widths = new Set<string>();

  props.matrixSizes?.forEach((skuDetailsArray) => {
    skuDetailsArray.forEach((skuDetails) => {
      widths.add(skuDetails.matrixSizeW);
    });
  });

  const widthsArray = Array.from(widths);

  // Check if all widths are numeric
  const areWidthsNumeric = widthsArray.every((width) => !isNaN(Number(width)));

  if (areWidthsNumeric) {
    // Sort numerically
    return widthsArray.sort((a, b) => Number(a) - Number(b));
  } else {
    // Sort by size labels
    const sizeOrder = [
      "XXS",
      "XS",
      "S",
      "M",
      "L",
      "XL",
      "XXL",
      "XXXL",
      "XXXXL",
    ];
    return widthsArray.sort(
      (a, b) => sizeOrder.indexOf(a) - sizeOrder.indexOf(b),
    );
  }
});

// Get in-stock pants lengths (different from available lengths)
const inStockPantsLengths = computed(() => {
  if (!props.matrixSizes) return [];
  let inStockpantsLengthsSet = new Set<string>();

  props.matrixSizes?.forEach((skuDetailsArray) => {
    skuDetailsArray.forEach((skuDetails) => {
      if (skuDetails.inventoryCount > 0) {
        inStockpantsLengthsSet.add(skuDetails.matrixSizeL);
      }
    });
  });

  // Convert the Set to an array and sort numerically
  return Array.from(inStockpantsLengthsSet).sort(
    (a, b) => Number(a) - Number(b),
  );
});

// Get in-stock pant widths (different from available widths)
const inStockPantsWidths = computed(() => {
  if (!props.matrixSizes) return [];
  let inStockpantsWidthsSet = new Set<string>();

  props.matrixSizes?.forEach((skuDetailsArray) => {
    skuDetailsArray.forEach((skuDetails) => {
      if (skuDetails.inventoryCount > 0) {
        inStockpantsWidthsSet.add(skuDetails.matrixSizeW);
      }
    });
  });

  // Convert the Set to an array and sort numerically
  return Array.from(inStockpantsWidthsSet).sort(
    (a, b) => Number(a) - Number(b),
  );
});

const soldOutSizes = computed<SkuDetails[]>(() => {
  return props.matrixSizes
    .flat()
    .filter((skuDetails) => skuDetails.inventoryCount === 0);
});

// Function to get SkuCode for a given width, length, and size
function getSkuCode(width: string, length: string) {
  let skuCode: string | undefined = undefined;

  props.matrixSizes?.forEach((skuDetailsArray) => {
    skuDetailsArray.forEach((skuDetails) => {
      if (
        skuDetails.matrixSizeW === width &&
        skuDetails.matrixSizeL === length
      ) {
        skuCode = skuDetails.skuCode;
      }
    });
  });

  return skuCode || "";
}

// Function to get Apptus ticket for a given width, length, and size
function getApptusTicket(width: string, length: string) {
  let ticket: string | undefined = undefined;

  props.matrixSizes?.forEach((skuDetailsArray) => {
    skuDetailsArray.forEach((skuDetails) => {
      if (
        skuDetails.matrixSizeW === width &&
        skuDetails.matrixSizeL === length
      ) {
        ticket = skuDetails.ticket;
      }
    });
  });

  return ticket || "";
}

// Function to get inventory count for a given width and length
function getInventoryCount(width: string, length: string) {
  let inventoryCount = "0";

  props.matrixSizes?.forEach((skuDetailsArray) => {
    skuDetailsArray.forEach((skuDetails) => {
      if (
        skuDetails.matrixSizeW === width &&
        skuDetails.matrixSizeL === length
      ) {
        inventoryCount = skuDetails.inventoryCount.toString();
      }
    });
  });

  return inventoryCount;
}

// Function to get available lengths for a given width and size
function getAvailableLengths(pantsWidth: string) {
  const availableLengths = new Set<string>();

  props.matrixSizes?.forEach((skuDetailsArray) => {
    skuDetailsArray.forEach((skuDetails) => {
      if (
        skuDetails.matrixSizeW === pantsWidth &&
        skuDetails.inventoryCount > 0 // Check inventory count
      ) {
        availableLengths.add(skuDetails.matrixSizeL);
      }
    });
  });

  return Array.from(availableLengths);
}

// Function to get available widths for a given length and size
function getAvailableWidths(pantsLength: string) {
  const availableWidths = new Set<string>();

  props.matrixSizes?.forEach((skuDetailsArray) => {
    skuDetailsArray.forEach((skuDetails) => {
      if (
        skuDetails.matrixSizeL === pantsLength &&
        skuDetails.inventoryCount > 0 // Check inventory count
      ) {
        availableWidths.add(skuDetails.matrixSizeW);
      }
    });
  });

  return Array.from(availableWidths);
}

// Reactive state for selected values
const selectedPantsWidth = ref<string>("");
const selectedPantsLength = ref<string>("");

// Reactive available options based on selected values
const availableWidths = ref<string[]>(pantsWidths.value);
const availableLengths = ref<string[]>(pantsLengths.value);

// Function to automatically select a single in-stock option if only one exists
const autoSelectSingleOption = () => {
  if (inStockPantsWidths.value.length === 1) {
    selectedPantsWidth.value = inStockPantsWidths.value[0];
    productPageStore.setWidthSelected(true);
  }
  if (inStockPantsLengths.value.length === 1) {
    selectedPantsLength.value = inStockPantsLengths.value[0];
    productPageStore.setLengthSelected(true);
  }

  if (selectedPantsWidth.value && selectedPantsLength.value) {
    productPageStore.setSkuCode(
      getSkuCode(selectedPantsWidth.value, selectedPantsLength.value),
    );
    productPageStore.setApptusTicket(
      getApptusTicket(selectedPantsWidth.value, selectedPantsLength.value),
    );
    productPageStore.setSize(
      selectedPantsWidth.value + selectedPantsLength.value
    );
    productPageStore.setInventoryCount(
      getInventoryCount(selectedPantsWidth.value, selectedPantsLength.value),
    );
    productPageStore.setCheckInventoryStatus(true);
  }
};

// Call the function when the component is mounted
onMounted(() => {
  autoSelectSingleOption();
});

// Watch for changes in both `selectedPantsWidth` and `selectedPantsLength`
watch([selectedPantsWidth, selectedPantsLength], ([newWidth, newLength]) => {
  if (newWidth && newLength) {
    // Update the SKU code and inventory count when both sizes are selected
    productPageStore.setSkuCode(getSkuCode(newWidth, newLength));
    productPageStore.setApptusTicket(getApptusTicket(newWidth, newLength));
    productPageStore.setSize(newWidth + newLength);
    productPageStore.setInventoryCount(getInventoryCount(newWidth, newLength));

    // Trigger inventory status check
    productPageStore.setCheckInventoryStatus(true);
  }
});

// Watchers to update available options based on selections
watch(
  [selectedPantsWidth, selectedPantsLength],
  () => {
    let tempAvailableWidths = pantsWidths.value;
    let tempAvailableLengths = pantsLengths.value;

    if (selectedPantsWidth.value) {
      // Update available lengths based on the selected width
      tempAvailableLengths = getAvailableLengths(selectedPantsWidth.value);
    }

    if (selectedPantsLength.value) {
      // Update available widths based on the selected length
      tempAvailableWidths = getAvailableWidths(selectedPantsLength.value);
    }

    // Set the reactive variables with the updated options
    availableWidths.value = tempAvailableWidths;
    availableLengths.value = tempAvailableLengths;
  },
  { immediate: true },
);

// Watchers to update the skuCode when width or length are selected/changes
watch(
  [selectedPantsWidth, selectedPantsLength],
  ([newWidth, newLength]) => {
    if (newWidth && newLength) {
      productPageStore.setSkuCode(getSkuCode(newWidth, newLength));
      productPageStore.setApptusTicket(getApptusTicket(newWidth, newLength));
      productPageStore.setSize(newWidth + newLength);
    }
  },
  { immediate: true },
);
</script>

<template>
  <div class="sizepicker sizepicker--pants">
    <!-- Widths Section -->
    <div class="size-tile-group">
      <!-- Width Information -->
      <div class="product__size-information">
        <span id="selectedsize" class="size-tile-group__group--title">
          <span v-if="!selectedPantsWidth">{{ $t("chooseWidth") }}:</span>
          <!-- Conditionally render the selected width when selected -->
          <span v-if="selectedPantsWidth">
            {{ $t("chooseWidth") }}:
            <strong>{{ selectedPantsWidth }}</strong>
          </span>
        </span>
        <!-- Size Guide Button (Conditional) -->
        <span v-if="isSizeGuide" class="product__size-guide">
          <button
            type="button"
            class="product__size-guide btn btn--small btn--link js-btn _js-vue-component-toggler"
            data-togglewhat="sizeguide"
          >
            {{ $t("sizeGuide") }}
          </button>
        </span>
      </div>

      <!-- Widths -->
      <!-- If width, size, and length are all selected,
        the input value gets populated with the skuCode -->
      <div class="size-tile-group__items size-tile-group__items--width">
        <label
          v-for="width in pantsWidths"
          :key="width"
          :class="[
            'size-tile-group__item',
            { 'size-tile-group__item--soldout': !availableWidths.includes(width) || !inStockPantsWidths.includes(width) }
          ]"
        >
          <input
            :id="'width_' + width"
            v-model="selectedPantsWidth"
            :name='$t("skuParameterCode")'
            :value="width"
            type="radio"
            :data-sku="
              selectedPantsWidth && selectedPantsLength
                ? getSkuCode(selectedPantsWidth, selectedPantsLength)
                : ''
            "
            class="size-tile-group__input _js-check-stock"
            :disabled="!availableWidths.includes(width) || !inStockPantsWidths.includes(width)"
            @change="productPageStore.setWidthSelected(true)"
          />
          <span class="size-tile-group__text">{{ width }}</span>
        </label>
      </div>
      <!-- Error Message if width not selected -->
      <span v-if="displayWidthNotSelectedError" class="size-tile-group__error">
        {{ $t("selectWidthMessage") }}
      </span>
    </div>

    <!-- Lengths Section -->
    <div class="size-tile-group">
      <!-- Length Information -->
      <div class="product__size-information">
        <span id="selectedsize" class="size-tile-group__group--title">
          <span v-if="!selectedPantsLength">{{ $t("chooseLength") }}:</span>
          <!-- Conditionally render the selected length when selected -->
          <span v-if="selectedPantsLength">
            {{ $t("chooseLength") }}:
            <strong>{{ selectedPantsLength }}</strong>
          </span>
        </span>
      </div>

      <!-- Lengths -->
      <div class="size-tile-group__items size-tile-group__items--length">
        <label
          v-for="length in pantsLengths"
          :key="length"
          :class="[
            'size-tile-group__item',
            { 'size-tile-group__item--soldout': !availableLengths.includes(length) || !inStockPantsLengths.includes(length) }
          ]"
        >
          <input
            :id="'length_' + length"
            v-model="selectedPantsLength"
            type="radio"
            :value="length"
            class="size-tile-group__input _js-check-stock"
            :disabled="!availableLengths.includes(length) || !inStockPantsLengths.includes(length)"
            @change="productPageStore.setLengthSelected(true)"
          />
          <span class="size-tile-group__text">{{ length }}</span>
        </label>
      </div>

      <!-- Error Message if length not selected -->
      <span v-if="displayLengthNotSelectedError" class="size-tile-group__error">
        {{ $t("selectLengthMessage") }}
      </span>
    </div>

    <!-- Inventory Low Notification -->
    <span v-if="productPageStore.inventoryIsLow" class="size-tile-group__warning">
      {{ productPageStore.inventoryLowMessage }}
    </span>

    <!-- Inventory Notification Button -->
    <div
      v-if="soldOutSizes.length > 0 && props.showInventoryNotification"
      class="product__inventory-notification _js-vue-inventory-nofitication-btn-container"
    >
      <button
        class="product__inventory-notification btn--link _js-vue-component-toggler _js-vue-inventory-notification-btn"
        data-togglewhat="inventoryNotification"
      >
        <Icon :type="IconTypes.Bell" :size="IconSizes.Tiny" />
        <p>
          {{ $t("inventoryNotificationTrigger") }}
        </p>
      </button>
    </div>
  </div>
</template>
